import { useMemo } from "react";
import {
  URL_PARAMS,
  DEFAULT_CAM_POS,
  DEFAULT_CAM_TARGET,
} from "./constants";

const parseTriple = (s) => {
  if (!s) return null;
  const parts = s.split(",").map((n) => parseFloat(n));
  if (parts.length !== 3 || !parts.every(Number.isFinite)) return null;
  return parts;
};

/**
 * useUrlSceneState — one-shot read of the URL query string at component mount.
 * Returns initial camera pose + time mode if present in URL, otherwise nulls.
 *
 * Format (Share-this-view):
 *   ?cam=x,y,z&tg=tx,ty,tz&t=auto|day|night
 */
export function useUrlSceneState() {
  return useMemo(() => {
    if (typeof window === "undefined") {
      return { camPos: null, targetPos: null, urlTimeMode: null };
    }
    const p = new URLSearchParams(window.location.search);
    const camPos = parseTriple(p.get(URL_PARAMS.cam));
    const targetPos = parseTriple(p.get(URL_PARAMS.target));
    const t = p.get(URL_PARAMS.time);
    const urlTimeMode =
      t === "auto" || t === "day" || t === "night" ? t : null;
    return { camPos, targetPos, urlTimeMode };
  }, []);
}

/** Build a Share-this-view URL from the current camera pose + time mode. */
export function buildShareUrl({ cam, target, timeMode }) {
  const params = new URLSearchParams();
  if (cam) params.set(URL_PARAMS.cam, cam.map((n) => n.toFixed(1)).join(","));
  if (target)
    params.set(URL_PARAMS.target, target.map((n) => n.toFixed(1)).join(","));
  if (timeMode) params.set(URL_PARAMS.time, timeMode);
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?${params.toString()}`;
}

export { DEFAULT_CAM_POS, DEFAULT_CAM_TARGET };
